/**
 * CONSAI Business App — Credit/Khatabook Page
 * Redesigned: Executive dark table aesthetic per user design prompt
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from '../components/ui/command';
import {
  Popover, PopoverContent, PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import {
  Plus, ArrowUpRight, ArrowDownLeft, CheckCircle, Search,
  ChevronLeft, ChevronRight, ChevronsUpDown, Check, UserPlus, Eye, Loader2, BookOpen
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import MobileNav from '../components/MobileNav';
import { AetherPage } from '../components/AetherPage';
import PlanExpiredModal from '../components/PlanExpiredModal';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => (n || 0).toLocaleString('en-IN');

const Credit = () => {
  const { language } = useLanguage();
  const { isPlanExpired } = useAuth();
  const [transactions, setTransactions] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalGiven, setTotalGiven] = useState(0);
  const [totalTaken, setTotalTaken] = useState(0);
  const [page, setPage] = useState(1);
  const [filterType, setFilterType] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [searchContact, setSearchContact] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showPlanExpired, setShowPlanExpired] = useState(false);
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [useManualEntry, setUseManualEntry] = useState(false);
  const [formData, setFormData] = useState({
    contact_name: '', contact_mobile: '', transaction_type: 'given', amount: '', description: '', customer_id: ''
  });

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 20 });
      if (filterType) params.append('transaction_type', filterType);
      if (filterStatus) params.append('status', filterStatus);
      if (searchContact) params.append('contact_name', searchContact);
      const res = await axios.get(`${API_URL}/api/credit?${params}`, { withCredentials: true });
      setTransactions(res.data.transactions);
      setTotal(res.data.total);
      setTotalGiven(res.data.total_given);
      setTotalTaken(res.data.total_taken);
    } catch (err) { toast.error('Failed to load transactions'); }
    setLoading(false);
  }, [page, filterType, filterStatus, searchContact]);

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/customers?per_page=100`, { withCredentials: true });
      setCustomers(res.data.customers || []);
    } catch { setCustomers([]); }
  };

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);
  useEffect(() => { fetchCustomers(); }, []);

  const openForm = () => {
    fetchCustomers();
    setFormData({ contact_name: '', contact_mobile: '', transaction_type: 'given', amount: '', description: '', customer_id: '' });
    setUseManualEntry(false);
    setShowForm(true);
  };

  const handleCustomerSelect = (customer) => {
    setFormData({ ...formData, contact_name: customer.name, contact_mobile: customer.mobile_number || '', customer_id: customer.customer_id });
    setCustomerDropdownOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.contact_name.trim()) { toast.error('Contact name is required'); return; }
    if (!formData.amount || Number(formData.amount) <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      await axios.post(`${API_URL}/api/credit`, {
        contact_name: formData.contact_name,
        contact_mobile: formData.contact_mobile || null,
        transaction_type: formData.transaction_type,
        amount: Number(formData.amount),
        description: formData.description || null,
        customer_id: formData.customer_id || null,
      }, { withCredentials: true });
      toast.success(language === 'hi' ? 'लेनदेन दर्ज हुआ' : 'Transaction recorded');
      if (window.triggerOnboardingComplete) window.triggerOnboardingComplete('credit');
      setShowForm(false);
      setFormData({ contact_name: '', contact_mobile: '', transaction_type: 'given', amount: '', description: '', customer_id: '' });
      fetchTransactions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  // Settlement state
  const [showSettleModal, setShowSettleModal] = useState(false);
  const [settlingTransaction, setSettlingTransaction] = useState(null);
  const [settlementType, setSettlementType] = useState('full');
  const [partialAmount, setPartialAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [settlementNote, setSettlementNote] = useState('');

  const openSettleModal = (transaction) => {
    setSettlingTransaction(transaction);
    setSettlementType('full');
    setPartialAmount('');
    setPaymentMethod('cash');
    setSettlementNote('');
    setShowSettleModal(true);
  };

  const handleSettlement = async () => {
    if (!settlingTransaction) return;
    const isPartial = settlementType === 'partial';
    const amount = isPartial ? Number(partialAmount) : settlingTransaction.amount;
    if (isPartial && (!amount || amount <= 0 || amount > settlingTransaction.amount)) {
      toast.error(language === 'hi' ? 'गलत राशि' : 'Invalid amount'); return;
    }
    try {
      await axios.put(`${API_URL}/api/credit/${settlingTransaction.transaction_id}/settle`, {
        settlement_type: settlementType, amount, payment_method: paymentMethod, note: settlementNote || null,
      }, { withCredentials: true });
      toast.success(isPartial
        ? (language === 'hi' ? `₹${amount} का भुगतान दर्ज` : `₹${amount} payment recorded`)
        : (language === 'hi' ? 'पूर्ण भुगतान दर्ज' : 'Full settlement recorded'));
      setShowSettleModal(false);
      setSettlingTransaction(null);
      fetchTransactions();
    } catch (err) { toast.error(err.response?.data?.detail || 'Settlement failed'); }
  };

  const totalPages = Math.ceil(total / 20);
  const netBalance = totalGiven - totalTaken;

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' • ' + d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: false });
  };

  const FILTER_PILLS = [
    { key: '', label: language === 'hi' ? 'सभी' : 'All' },
    { key: 'given', label: language === 'hi' ? 'दिया' : 'Given' },
    { key: 'taken', label: language === 'hi' ? 'लिया' : 'Taken' },
    { key: '__pending', label: language === 'hi' ? 'बकाया' : 'Pending' },
  ];

  const activeFilter = filterStatus === 'pending' ? '__pending' : filterType;

  const handlePillClick = (key) => {
    if (key === '__pending') { setFilterType(''); setFilterStatus('pending'); }
    else { setFilterType(key); setFilterStatus(''); }
    setPage(1);
  };

  const creditAddBtn = (
    <button
      onClick={() => {
        if (isPlanExpired()) { setShowPlanExpired(true); return; }
        setFormData({ contact_name: '', contact_mobile: '', transaction_type: 'given', amount: '', description: '' });
        setShowForm(true);
      }}
      className="flex items-center gap-2 px-5 py-2.5 rounded-sm font-bold text-xs tracking-widest uppercase text-[#0A0A0B] bg-white hover:bg-white/90 active:scale-95 transition-all"
      data-testid="add-credit-btn"
    >
      <Plus className="h-4 w-4" />
      {language === 'hi' ? 'नया लेनदेन' : 'NEW ENTRY'}
    </button>
  );

  return (
    <AetherPage
      title={language === 'hi' ? 'उधार/खाताबुक' : 'Credit/Khatabook'}
      icon={BookOpen}
      iconColor="#ffffff"
      titleTestId="credit-title"
      subtitle={language === 'hi' ? 'क्रेडिट जीवनचक्र प्रबंधन। रियल-टाइम कैशफ्लो।' : 'Enterprise-grade credit lifecycle management. Monitor real-time cash flow and receivables.'}
      rightElement={creditAddBtn}
      mainClassName="px-4 md:px-8 py-6 md:py-8 space-y-8"
    >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {/* Total Given */}
            <div
              className="relative overflow-hidden rounded-sm p-6"
              style={{ background: '#111111', border: '1px solid #262626' }}
              data-testid="credit-given-card"
            >
              <div className="absolute top-0 left-0 w-1 h-full" style={{ background: '#EF4444', opacity: 0.7 }} />
              <div className="flex justify-between items-start mb-4">
                <span className="text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'कुल दिया' : 'Total Given'}
                </span>
                <ArrowUpRight className="h-5 w-5 text-[#EF4444]" />
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl md:text-4xl font-bold text-[#EF4444]">₹{fmt(totalGiven)}</p>
              </div>
              <p className="text-xs text-[#8A8A93] mt-1">
                {language === 'hi' ? 'बकाया प्राप्य' : 'Outstanding receivable'}
              </p>
            </div>

            {/* Total Taken */}
            <div
              className="relative overflow-hidden rounded-sm p-6"
              style={{ background: '#111111', border: '1px solid #262626' }}
              data-testid="credit-taken-card"
            >
              <div className="absolute top-0 left-0 w-1 h-full" style={{ background: '#22C55E', opacity: 0.7 }} />
              <div className="flex justify-between items-start mb-4">
                <span className="text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'कुल लिया' : 'Total Taken'}
                </span>
                <ArrowDownLeft className="h-5 w-5 text-[#22C55E]" />
              </div>
              <div className="flex items-baseline gap-2">
                <p className="text-3xl md:text-4xl font-bold text-[#22C55E]">₹{fmt(totalTaken)}</p>
                <span className="text-sm text-[#8A8A93]">
                  {totalTaken === 0 ? 'No liabilities' : ''}
                </span>
              </div>
              <p className="text-xs text-[#8A8A93] mt-1">
                {language === 'hi' ? 'बकाया देय' : 'Outstanding payable'}
              </p>
            </div>

            {/* Net Balance */}
            <div
              className="relative overflow-hidden rounded-sm p-6"
              style={{ background: '#171717', border: '1px solid #262626' }}
              data-testid="credit-net-card"
            >
              <div
                className="absolute top-0 left-0 w-1 h-full"
                style={{ background: '#F4F4F5', opacity: 0.5 }}
              />
              <div className="flex justify-between items-start mb-4">
                <span className="text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'शुद्ध शेष' : 'Net Balance'}
                </span>
              </div>
              <p className="text-3xl md:text-4xl font-bold text-[#F4F4F5]">
                ₹{fmt(Math.abs(netBalance))}
              </p>
              <span className="text-xs font-bold tracking-widest uppercase text-[#22C55E] mt-1 block">
                {netBalance >= 0
                  ? (language === 'hi' ? 'प्राप्त होगा' : 'To Receive')
                  : (language === 'hi' ? 'देना होगा' : 'To Pay')}
              </span>
            </div>
          </div>

          {/* ── Search & Filters ── */}
          <div
            className="flex flex-wrap items-center justify-between gap-4 p-4 md:p-6 rounded-sm border border-[#262626]"
            style={{ background: '#0D0D0D' }}
          >
            <div className="relative flex-1 min-w-[200px] max-w-lg">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8A8A93]" />
              <input
                type="text"
                placeholder={language === 'hi' ? 'नाम या संदर्भ ID से फ़िल्टर करें...' : 'Filter by contact name or reference ID...'}
                value={searchContact}
                onChange={(e) => { setSearchContact(e.target.value); setPage(1); }}
                className="w-full bg-[#111111] border border-[#262626] rounded-sm py-3 pl-12 pr-4 text-sm text-[#F4F4F5] placeholder-[#8A8A93] outline-none focus:border-[#F4F4F5] transition-all"
                data-testid="credit-search-input"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {FILTER_PILLS.map((pill) => (
                <button
                  key={pill.key}
                  onClick={() => handlePillClick(pill.key)}
                  className={`px-5 py-2 rounded-full text-xs font-bold tracking-widest uppercase transition-all ${
                    activeFilter === pill.key
                      ? 'bg-white text-[#0A0A0B]'
                      : 'border border-[#262626] text-[#8A8A93] hover:border-white/30 hover:text-[#F4F4F5]'
                  }`}
                >
                  {pill.label}
                </button>
              ))}
            </div>
          </div>

          {/* ── Transaction List ── */}
          {loading ? (
            <div className="flex justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-[#8A8A93]" />
            </div>
          ) : transactions.length === 0 ? (
            <div
              className="text-center py-16 rounded-sm border border-[#262626]"
              style={{ background: '#111111' }}
            >
              <p className="text-[#8A8A93] text-sm">
                {language === 'hi' ? 'कोई लेनदेन नहीं' : 'No transactions found'}
              </p>
            </div>
          ) : (
            <div
              className="rounded-sm border border-[#262626] overflow-hidden"
              style={{ background: '#0D0D0D' }}
              data-testid="credit-list"
            >
              {/* Table Header — desktop only */}
              <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 md:px-8 py-4 border-b border-[#262626] bg-[#111111]">
                <p className="col-span-4 text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'संपर्क और संदर्भ' : 'Counterparty & Reference'}
                </p>
                <p className="col-span-3 text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'तारीख' : 'Timestamp'}
                </p>
                <p className="col-span-2 text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'स्थिति' : 'Status'}
                </p>
                <p className="col-span-2 text-right text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'राशि' : 'Magnitude'}
                </p>
                <p className="col-span-1 text-right text-xs font-bold tracking-widest uppercase text-[#8A8A93]">
                  {language === 'hi' ? 'क्रिया' : 'Actions'}
                </p>
              </div>

              {transactions.map((t) => {
                const isGiven = t.transaction_type === 'given';
                const isSettled = t.status === 'settled';
                const amtColor = isSettled ? '#22C55E' : isGiven ? '#EF4444' : '#22C55E';
                const amtPrefix = isSettled ? '+' : isGiven ? '−' : '+';

                return (
                  <div
                    key={t.transaction_id}
                    className={`border-b border-[#1A1A1A] hover:bg-white/[0.02] transition-colors ${isSettled ? 'opacity-60' : ''}`}
                    data-testid={`credit-card-${t.transaction_id}`}
                  >
                    {/* Desktop layout */}
                    <div className="hidden md:grid md:grid-cols-12 items-center gap-4 px-8 py-5">
                      {/* Counterparty */}
                      <div className="col-span-4 flex items-center gap-4">
                        <div
                          className="w-10 h-10 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: isSettled ? 'rgba(34,197,94,0.1)' : isGiven ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)' }}
                        >
                          {isSettled
                            ? <ArrowDownLeft className="h-5 w-5 text-[#22C55E]" />
                            : isGiven
                            ? <ArrowUpRight className="h-5 w-5 text-[#EF4444]" />
                            : <ArrowDownLeft className="h-5 w-5 text-[#22C55E]" />
                          }
                        </div>
                        <div>
                          <p className="text-base font-bold text-[#F4F4F5] leading-none mb-1">{t.contact_name}</p>
                          <p className="text-xs text-[#8A8A93]">
                            {t.description || (isGiven ? 'Credit given' : 'Credit taken')}
                          </p>
                        </div>
                      </div>

                      {/* Timestamp */}
                      <div className="col-span-3 text-sm text-[#9CA3AF]">
                        {formatDate(t.created_at)}
                      </div>

                      {/* Status */}
                      <div className="col-span-2">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold tracking-widest uppercase border"
                          style={isSettled
                            ? { background: 'rgba(34,197,94,0.1)', color: '#22C55E', borderColor: 'rgba(34,197,94,0.25)' }
                            : { background: 'rgba(239,68,68,0.1)', color: '#EF4444', borderColor: 'rgba(239,68,68,0.25)' }
                          }
                        >
                          {t.status === 'settled' ? 'SETTLED' : 'PENDING'}
                        </span>
                      </div>

                      {/* Amount */}
                      <div className="col-span-2 text-right">
                        <p className="text-lg font-bold" style={{ color: amtColor }}>
                          {amtPrefix} ₹{t.amount.toLocaleString('en-IN')}
                        </p>
                      </div>

                      {/* Actions */}
                      <div className="col-span-1 flex justify-end gap-3 text-[#8A8A93]">
                        <button className="hover:text-[#F4F4F5] transition-colors" title="View">
                          <Eye className="h-5 w-5" />
                        </button>
                        {t.status === 'pending' && (
                          <button
                            onClick={() => openSettleModal(t)}
                            className="hover:text-[#22C55E] transition-colors"
                            title="Settle"
                            data-testid={`settle-${t.transaction_id}`}
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Mobile layout */}
                    <div className="md:hidden flex items-center justify-between px-4 py-4">
                      <div className="flex items-center gap-3">
                        <div
                          className="w-9 h-9 rounded-full flex items-center justify-center shrink-0"
                          style={{ background: isGiven ? 'rgba(239,68,68,0.1)' : 'rgba(34,197,94,0.1)' }}
                        >
                          {isGiven
                            ? <ArrowUpRight className="h-4 w-4 text-[#EF4444]" />
                            : <ArrowDownLeft className="h-4 w-4 text-[#22C55E]" />
                          }
                        </div>
                        <div>
                          <p className="text-sm font-bold text-[#F4F4F5]">{t.contact_name}</p>
                          <p className="text-xs text-[#8A8A93]">{formatDate(t.created_at).split('•')[0]}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="text-right">
                          <p className="text-sm font-bold" style={{ color: amtColor }}>
                            {amtPrefix} ₹{t.amount.toLocaleString('en-IN')}
                          </p>
                          <span
                            className="text-xs font-bold tracking-wider uppercase"
                            style={{ color: isSettled ? '#22C55E' : '#EF4444' }}
                          >
                            {t.status === 'settled' ? 'SETTLED' : 'PENDING'}
                          </span>
                        </div>
                        {t.status === 'pending' && (
                          <button
                            onClick={() => openSettleModal(t)}
                            className="text-[#22C55E] ml-2"
                            data-testid={`settle-${t.transaction_id}`}
                          >
                            <CheckCircle className="h-5 w-5" />
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between text-[#8A8A93] pt-2">
              <p className="text-sm">
                {language === 'hi'
                  ? `${total} लेनदेन में से ${Math.min(page * 20, total)} दिखाए`
                  : `Showing ${Math.min(page * 20, total)} of ${total} ledger entries`}
              </p>
              <div className="flex items-center gap-2">
                <button
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                  className="p-2 border border-[#262626] hover:border-white/30 rounded-sm disabled:opacity-40 transition-colors"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                  className="p-2 border border-[#262626] hover:border-white/30 rounded-sm disabled:opacity-40 transition-colors"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

      {/* ── New Transaction Dialog ── */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{language === 'hi' ? 'नया लेनदेन' : 'New Transaction'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="flex gap-2">
              <Button type="button" variant={formData.transaction_type === 'given' ? 'default' : 'outline'}
                onClick={() => setFormData({ ...formData, transaction_type: 'given' })}
                className={`flex-1 ${formData.transaction_type === 'given' ? 'bg-[#EF4444] hover:bg-[#EF4444]/90 text-white' : ''}`} data-testid="type-given-btn">
                <ArrowUpRight className="h-4 w-4 mr-1" />{language === 'hi' ? 'उधार दिया' : 'Given'}
              </Button>
              <Button type="button" variant={formData.transaction_type === 'taken' ? 'default' : 'outline'}
                onClick={() => setFormData({ ...formData, transaction_type: 'taken' })}
                className={`flex-1 ${formData.transaction_type === 'taken' ? 'bg-green-600 hover:bg-green-700' : ''}`} data-testid="type-taken-btn">
                <ArrowDownLeft className="h-4 w-4 mr-1" />{language === 'hi' ? 'उधार लिया' : 'Taken'}
              </Button>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">{language === 'hi' ? 'संपर्क' : 'Contact'}</label>
                <Button type="button" variant="ghost" size="sm"
                  onClick={() => { setUseManualEntry(!useManualEntry); if (!useManualEntry) setFormData({ ...formData, customer_id: '', contact_name: '', contact_mobile: '' }); }}
                  className="text-xs h-6 px-2" data-testid="toggle-manual-entry">
                  <UserPlus className="h-3 w-3 mr-1" />
                  {useManualEntry ? (language === 'hi' ? 'ग्राहक चुनें' : 'Select Customer') : (language === 'hi' ? 'नया जोड़ें' : 'Add New')}
                </Button>
              </div>
              {useManualEntry ? (
                <>
                  <Input placeholder={language === 'hi' ? 'संपर्क का नाम *' : 'Contact Name *'} value={formData.contact_name}
                    onChange={(e) => setFormData({ ...formData, contact_name: e.target.value, customer_id: '' })} data-testid="credit-contact-input" />
                  <Input placeholder={language === 'hi' ? 'मोबाइल नंबर' : 'Mobile Number'} value={formData.contact_mobile}
                    onChange={(e) => setFormData({ ...formData, contact_mobile: e.target.value })} />
                </>
              ) : (
                <Popover open={customerDropdownOpen} onOpenChange={setCustomerDropdownOpen}>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" role="combobox" aria-expanded={customerDropdownOpen}
                      className="w-full justify-between font-normal" data-testid="customer-dropdown-trigger">
                      {formData.contact_name || (language === 'hi' ? 'ग्राहक चुनें...' : 'Select customer...')}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[300px] p-0" align="start" sideOffset={4}>
                    <Command shouldFilter={true}>
                      <CommandInput placeholder={language === 'hi' ? 'ग्राहक खोजें...' : 'Search customer...'} />
                      <CommandList>
                        {customers.length === 0 ? (
                          <div className="py-4 text-center">
                            <p className="text-sm text-slate-500">{language === 'hi' ? 'कोई ग्राहक नहीं' : 'No customers yet'}</p>
                            <Button type="button" variant="link" size="sm" onClick={() => { setUseManualEntry(true); setCustomerDropdownOpen(false); }} className="text-xs mt-1">
                              <UserPlus className="h-3 w-3 mr-1" />{language === 'hi' ? 'नया जोड़ें' : 'Add manually'}
                            </Button>
                          </div>
                        ) : (
                          <>
                            <CommandEmpty>
                              <div className="py-2 text-center">
                                <p className="text-sm text-slate-500">{language === 'hi' ? 'कोई मिलान नहीं' : 'No match found'}</p>
                                <Button type="button" variant="link" size="sm" onClick={() => { setUseManualEntry(true); setCustomerDropdownOpen(false); }} className="text-xs">
                                  <UserPlus className="h-3 w-3 mr-1" />{language === 'hi' ? 'नया जोड़ें' : 'Add manually'}
                                </Button>
                              </div>
                            </CommandEmpty>
                            <CommandGroup heading={language === 'hi' ? 'ग्राहक' : 'Customers'}>
                              {customers.map((customer) => (
                                <CommandItem key={customer.customer_id} value={customer.name} onSelect={() => handleCustomerSelect(customer)} className="cursor-pointer" data-testid={`customer-option-${customer.customer_id}`}>
                                  <Check className={`mr-2 h-4 w-4 ${formData.customer_id === customer.customer_id ? 'opacity-100' : 'opacity-0'}`} />
                                  <div className="flex flex-col">
                                    <span className="font-medium">{customer.name}</span>
                                    {customer.mobile_number && <span className="text-xs text-slate-500">{customer.mobile_number}</span>}
                                  </div>
                                </CommandItem>
                              ))}
                            </CommandGroup>
                          </>
                        )}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              )}
            </div>

            <Input type="number" placeholder={language === 'hi' ? 'राशि (₹) *' : 'Amount (₹) *'} value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: e.target.value })} data-testid="credit-amount-input" className="text-lg" />
            <Input placeholder={language === 'hi' ? 'विवरण' : 'Description'} value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>{language === 'hi' ? 'रद्द करें' : 'Cancel'}</Button>
              <Button type="submit" className="bg-[#021945] hover:bg-[#021945]/90" data-testid="credit-submit-btn">{language === 'hi' ? 'दर्ज करें' : 'Record'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* ── Settlement Modal ── */}
      <Dialog open={showSettleModal} onOpenChange={setShowSettleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{language === 'hi' ? 'भुगतान दर्ज करें' : 'Record Payment'}</DialogTitle>
          </DialogHeader>
          {settlingTransaction && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="font-medium text-slate-900">{settlingTransaction.contact_name}</p>
                <p className="text-sm text-slate-500">
                  {language === 'hi' ? 'बकाया राशि' : 'Outstanding'}:
                  <span className="font-semibold text-[#EF4444] ml-1">₹{settlingTransaction.amount.toLocaleString('en-IN')}</span>
                </p>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{language === 'hi' ? 'भुगतान प्रकार' : 'Settlement Type'}</label>
                <div className="flex gap-2">
                  <Button type="button" variant={settlementType === 'full' ? 'default' : 'outline'}
                    onClick={() => setSettlementType('full')}
                    className={`flex-1 ${settlementType === 'full' ? 'bg-green-600 hover:bg-green-700' : ''}`} data-testid="settlement-full-btn">
                    {language === 'hi' ? 'पूर्ण भुगतान' : 'Full Settlement'}
                  </Button>
                  <Button type="button" variant={settlementType === 'partial' ? 'default' : 'outline'}
                    onClick={() => setSettlementType('partial')}
                    className={`flex-1 ${settlementType === 'partial' ? 'bg-amber-600 hover:bg-amber-700' : ''}`} data-testid="settlement-partial-btn">
                    {language === 'hi' ? 'आंशिक भुगतान' : 'Partial Payment'}
                  </Button>
                </div>
              </div>
              {settlementType === 'partial' && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-700">{language === 'hi' ? 'प्राप्त राशि' : 'Amount Received'}</label>
                  <Input type="number" placeholder={`Max ₹${settlingTransaction.amount}`} value={partialAmount}
                    onChange={(e) => setPartialAmount(e.target.value)} max={settlingTransaction.amount} className="text-lg" data-testid="partial-amount-input" />
                  {partialAmount && Number(partialAmount) < settlingTransaction.amount && (
                    <p className="text-xs text-amber-600">{language === 'hi' ? 'शेष' : 'Remaining'}: ₹{(settlingTransaction.amount - Number(partialAmount)).toLocaleString('en-IN')}</p>
                  )}
                </div>
              )}
              <div className="space-y-2">
                <label className="text-sm font-medium text-slate-700">{language === 'hi' ? 'भुगतान का तरीका' : 'Payment Method'}</label>
                <div className="grid grid-cols-4 gap-2">
                  {['cash', 'upi', 'bank', 'other'].map((method) => (
                    <Button key={method} type="button" variant={paymentMethod === method ? 'default' : 'outline'} size="sm"
                      onClick={() => setPaymentMethod(method)} className={`text-xs ${paymentMethod === method ? 'bg-[#021945]' : ''}`}>
                      {method === 'cash' ? (language === 'hi' ? 'नकद' : 'Cash') : method === 'upi' ? 'UPI' : method === 'bank' ? (language === 'hi' ? 'बैंक' : 'Bank') : (language === 'hi' ? 'अन्य' : 'Other')}
                    </Button>
                  ))}
                </div>
              </div>
              <Input placeholder={language === 'hi' ? 'नोट (वैकल्पिक)' : 'Note (optional)'} value={settlementNote} onChange={(e) => setSettlementNote(e.target.value)} />
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setShowSettleModal(false)}>{language === 'hi' ? 'रद्द करें' : 'Cancel'}</Button>
                <Button onClick={handleSettlement} className="bg-green-600 hover:bg-green-700" data-testid="confirm-settlement-btn">
                  {language === 'hi' ? 'भुगतान दर्ज करें' : 'Confirm Payment'}
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <PlanExpiredModal open={showPlanExpired} onClose={() => setShowPlanExpired(false)} />
    </AetherPage>
  );
};

export default Credit;
